import {
  startTrackingMedTaskActivity,
  stopTrackingMedTaskActivity,
  stopTrackingMedTaskActivityOnUnload,
  trackNavigation
} from '@/shared/api/services/activity-log';
import { getRpmWorkflowSessionType } from '@/shared/lib/util/rpm';
import { useRpmWorkflowStore } from '@/shared/model';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

type MedTaskLog = { id: string; patientId: string };

const ActivityTracker = () => {
  const { pathname } = useLocation();
  const { id } = useParams();
  const previousActivityIdRef = useRef('');
  const { setActiveType, setStopMedTaskTrackingFn } = useRpmWorkflowStore();

  const rpmWorkflowSessionType = getRpmWorkflowSessionType();

  const patientId = id && pathname.split(`/patients/${id}`).length > 1 ? id : undefined;
  const latestPatientIdRef = useRef(patientId);
  latestPatientIdRef.current = patientId;
  const openLogRef = useRef<MedTaskLog | null>(null);
  const switchQueueRef = useRef<Promise<void>>(Promise.resolve());

  const track = useCallback(async () => {
    console.warn('track', 'navigateTo:', location.pathname, 'previousActivityId:', previousActivityIdRef.current);

    const activityLog = id
      ? await trackNavigation({
          navigateTo: location.pathname,
          prevActivityLogId: previousActivityIdRef.current,
          truentityId: id
        })
      : null;

    if (activityLog) {
      previousActivityIdRef.current = activityLog.id;
    }
  }, [id]);

  const switchMedTaskLog = useCallback((nextPatientId: string | undefined) => {
    const run = async () => {
      const openLog = openLogRef.current;
      if (openLog?.patientId === nextPatientId) return;

      if (openLog) {
        openLogRef.current = null;
        await stopTrackingMedTaskActivity({ activityLogId: openLog.id });
      }

      if (nextPatientId && latestPatientIdRef.current === nextPatientId) {
        const activityLog = await startTrackingMedTaskActivity({ truentityId: nextPatientId });
        if (activityLog) {
          openLogRef.current = { id: activityLog.id, patientId: nextPatientId };
        }
      }
    };

    const queued = switchQueueRef.current.then(run).catch(console.error);
    switchQueueRef.current = queued;
    return queued;
  }, []);

  const stopMedTaskTracking = useCallback(() => switchMedTaskLog(undefined), [switchMedTaskLog]);

  useEffect(() => {
    setActiveType(rpmWorkflowSessionType);
  }, [rpmWorkflowSessionType, setActiveType]);

  useEffect(() => {
    switchMedTaskLog(patientId).catch(console.error);
  }, [patientId, switchMedTaskLog]);

  useEffect(() => {
    track().catch(console.error);
  }, [pathname, track]);

  useEffect(() => {
    const onPageHide = () => {
      const openLog = openLogRef.current;
      if (openLog) {
        openLogRef.current = null;
        stopTrackingMedTaskActivityOnUnload({ activityLogId: openLog.id });
      }
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        switchMedTaskLog(latestPatientIdRef.current).catch(console.error);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [switchMedTaskLog]);

  useEffect(() => {
    setStopMedTaskTrackingFn(stopMedTaskTracking);

    return () => {
      setStopMedTaskTrackingFn(null);
    };
  }, [setStopMedTaskTrackingFn, stopMedTaskTracking]);

  return null;
};

export default ActivityTracker;
